<template>
  <v-chip v-on="$listeners"
          class="nx-chip ma-0 mr-1"
          :class="{ actionable, [size]: true }"
          :text-color="calculatedTextColor"
          :color="calculatedColor">
    <v-avatar v-if="image" size="16px" color="grey lighten-1">
      <img :src="image.src" :alt="image.alt">
    </v-avatar>
    <v-icon v-else-if="icon" size="16px" v-text="icon"></v-icon>
    <slot>
      <a v-if="url" :href="url"><strong v-text="calculatedValue"></strong></a>
      <strong v-else v-text="calculatedValue"></strong>
    </slot>
  </v-chip>
</template>

<script>
import moment from 'moment';

const COLOR_MAP = {
  lightGrey: '#e9e9f2',
  yellow: '#fcde83',
  green: '#cef3e5',
  orange: '#fcde83',
  blue: '#4e82d1',
  grey: '#676775',
  black: '#41414e',
  white: '#ffffff',
};

export default {
  name: 'nx-chip',
  props: {
    // Variants https://app.zeplin.io/project/5a3b96ad5fac4253c4adfff2/screen/619ccdeebda825111df55f10
    default: {
      type: Boolean,
    },
    alert: {
      type: Boolean,
    },
    success: {
      type: Boolean,
    },
    info: {
      type: Boolean,
    },
    // Sizes
    medium: {
      type: Boolean,
    },
    large: {
      type: Boolean,
    },
    // Other props
    dark: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Number, String],
    },
    textColor: {
      type: String,
    },
    color: {
      type: String,
    },
    icon: {
      type: String,
    },
    /**
     * {
     *   src: {String}
     *   alt: {String}
     * }
     */
    image: {
      type: Object,
    },
    url: {
      type: String,
    },
    date: {
      type: Boolean,
    },
    humanDate: {
      type: Boolean,
    },
    translationVariables: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    size() {
      if (this.medium) {
        return 'medium';
      }
      if (this.large) {
        return 'large';
      }
      return 'x-small';
    },
    actionable() {
      return this.url || this.$listeners.click || this.$listeners['!click'];
    },
    calculatedValue() {
      if (!this.value) {
        return this.value;
      }
      if (this.humanDate) {
        return this.$options.filters.formatDateFromNow(this.value);
      }
      if (this.date) {
        return moment(this.value).format('DD/MM/YYYY - H:mm');
      }
      const pluralizeValue = Object.values(this.translationVariables)[0] || 1;
      if (this.$te(`message.${this.value}`)) {
        return this.$tc(`message.${this.value}`, pluralizeValue, this.translationVariables);
      }
      return this.value;
    },
    calculatedColor() {
      if (this.color) {
        return COLOR_MAP[this.color] || this.color;
      }
      if (this.default) {
        return this.dark ? COLOR_MAP.grey : COLOR_MAP.lightGrey;
      }
      if (this.alert) {
        return COLOR_MAP.orange;
      }
      if (this.success) {
        return COLOR_MAP.green;
      }
      if (this.info) {
        return COLOR_MAP.blue;
      }
      return this.dark ? COLOR_MAP.grey : COLOR_MAP.lightGrey;
    },
    calculatedTextColor() {
      if (this.textColor) {
        return COLOR_MAP[this.textColor] || this.textColor;
      }
      const whiteTextColors = [
        COLOR_MAP.grey,
        COLOR_MAP.black,
        COLOR_MAP.blue,
      ];
      if (whiteTextColors.includes(this.calculatedColor)) {
        return COLOR_MAP.white;
      }
      return COLOR_MAP.black;
    },
  },
  methods: {
    capitalize(val) {
      return val.toString().charAt(0).toUpperCase() + val.slice(1);
    },
  },
};
</script>

<style lang="stylus">
.nx-chip {
  &.actionable .v-chip__content {
    cursor: pointer;

    &:hover {
      opacity: .5;
    }
  }

  &.v-chip {
    .v-chip__content {
      > * {
        max-width: 32vw;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      a {
        color: inherit;
        text-decoration: inherit;

      }
    }

    //  X-small size
    &.x-small {
      height: 18px !important;
      .v-avatar {
        display: none;
      }
      .v-chip__content {
        border-radius: 10px
        height: 18px;
        font-size: 12px;
        font-weight: normal;
        padding: 0 8px;
        line-height: 1;
      }
    }

    &.medium {
      height: 24px !important;
      .v-chip__content {
        border-radius: 12px
        height: 24px;
        font-size: 14px;
        font-weight: normal;
        line-height: 0.86;
        padding: 0 12px;
      }
    }

    &.large {
      height: 32px !important;
      .v-chip__content {
        border-radius: 16px
        height: 32px;
        font-size: 16px;
        font-weight: normal;
        line-height: 0.75;
        padding: 0 12px;
      }
    }
  }
}
</style>
